import React, { useCallback, useEffect, useState } from 'react';
import { Product } from '../data/mockProducts';
interface CartItem {
  product: Product;
  quantity: number;
}
interface CartState {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
}
const CART_STORAGE_KEY = 'diamond-mart-cart';
function loadCartFromStorage(): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}
function saveCartToStorage(items: CartItem[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Storage full or unavailable
  }
}
export function useCart() {
  const [items, setItems] = useState<CartItem[]>(() => loadCartFromStorage());
  useEffect(() => {
    saveCartToStorage(items);
  }, [items]);
  const addToCart = useCallback((product: Product, quantity: number = 1) => {
    setItems(current => {
      const existingIndex = current.findIndex(item => item.product.id === product.id);
      if (existingIndex >= 0) {
        const updated = [...current];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity
        };
        return updated;
      }
      return [...current, {
        product,
        quantity
      }];
    });
  }, []);
  const removeFromCart = useCallback((productId: string) => {
    setItems(current => current.filter(item => item.product.id !== productId));
  }, []);
  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setItems(current => current.map(item => item.product.id === productId ? {
      ...item,
      quantity
    } : item));
  }, [removeFromCart]);
  const clearCart = useCallback(() => {
    setItems([]);
  }, []);
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const getItemQuantity = useCallback((productId: string) => {
    const item = items.find(i => i.product.id === productId);
    return item?.quantity || 0;
  }, [items]);
  return {
    items,
    totalItems,
    totalPrice,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getItemQuantity
  };
}